import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useApp } from "./AppContext";
import { useSalon } from "./SalonContext";
import {
  CreateDiscountPresetRequest,
  DeleteDiscountPresetResponse,
  DiscountPreset,
  UpdateDiscountPresetRequest,
} from "../types/discounts";
import {
  getQuickDiscounts,
  sortDiscountPresets,
  withQuickDiscountOrder,
} from "../utils/discountPresetUtils";

export interface DiscountPresetsState {
  /** Active presets at the current location, ordered by name. */
  presets: DiscountPreset[];
  /** True while the list GET is in flight. */
  loading: boolean;
  /** Set when the list GET failed; cleared by a successful refresh. */
  error: unknown | null;
  /** Re-fetch the list (e.g. a Retry button). */
  refresh: () => Promise<void>;
  /** POST a preset; on success it's merged into `presets` (name order kept). */
  createPreset: (body: CreateDiscountPresetRequest) => Promise<DiscountPreset>;
  /** PUT a preset; on success it replaces the matching entry in `presets`. */
  updatePreset: (
    presetId: string,
    body: UpdateDiscountPresetRequest,
  ) => Promise<DiscountPreset>;
  /**
   * DELETE a preset. Whether the backend archived it (`mode: "soft"`, it was
   * used on past tickets) or deleted it (`mode: "hard"`), it leaves the active
   * list, so it's filtered out of `presets` either way.
   */
  removePreset: (presetId: string) => Promise<DeleteDiscountPresetResponse>;
  /** The quick discounts (up to three), in display order. */
  quickDiscounts: DiscountPreset[];
  /**
   * PUT the quick discounts as `presetIds`, in display order (`[]` clears).
   * Applied optimistically so a drag-reorder lands instantly; rolled back,
   * and the promise rejected, when the request fails.
   */
  setQuickDiscounts: (presetIds: string[]) => Promise<void>;
}

const unavailable = () =>
  Promise.reject(new Error("Discount presets are unavailable."));

/** State surfaced when no provider is mounted (e.g. isolated unit tests):
 *  an empty, idle catalog whose mutations reject. */
const INERT_STATE: DiscountPresetsState = {
  presets: [],
  loading: false,
  error: null,
  refresh: async () => {},
  createPreset: unavailable,
  updatePreset: unavailable,
  removePreset: unavailable,
  quickDiscounts: [],
  setQuickDiscounts: unavailable,
};

const DiscountPresetsContext = createContext<DiscountPresetsState>(INERT_STATE);

/**
 * App-root discount presets catalog for the current location. Fires ONE GET
 * per location as soon as businessInfo.id resolves, and refetches on location
 * switch. Settings → Discounts and every apply-discount modal read the same
 * state, and every mutation goes through here and updates it in place — so a
 * preset created inline on a ticket shows up in Settings (and any other open
 * modal) immediately, without a refetch.
 *
 * Skipped (no fetch) until a location is selected.
 */
export function DiscountPresetsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const {
    getDiscountPresets,
    createDiscountPreset,
    updateDiscountPreset,
    deleteDiscountPreset,
    setQuickDiscounts: putQuickDiscounts,
  } = useApp();
  const { businessInfo } = useSalon() as {
    businessInfo?: { id?: string } | null;
  };

  const salonId = businessInfo?.id ?? null;

  const [presets, setPresets] = useState<DiscountPreset[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<unknown | null>(null);

  // Tracks the location the state belongs to, so a response (list or
  // mutation) that resolves after a location switch never lands on the new
  // location's catalog. `requestIdRef` additionally drops a superseded GET.
  const salonIdRef = useRef<string | null>(salonId);
  salonIdRef.current = salonId;
  // Latest catalog, for rolling back an optimistic quick-discounts update.
  const presetsRef = useRef(presets);
  presetsRef.current = presets;
  const requestIdRef = useRef(0);

  const refresh = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    if (!salonId) {
      setPresets([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const list = await getDiscountPresets(salonId);
      if (requestId !== requestIdRef.current) return;
      setPresets(sortDiscountPresets(list ?? []));
      setError(null);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error("Error loading discount presets:", err);
      setError(err);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [salonId, getDiscountPresets]);

  useEffect(() => {
    // New location (or first mount): drop the previous catalog before loading.
    setPresets([]);
    setError(null);
    refresh();
  }, [refresh]);

  const requireSalonId = useCallback((): string => {
    if (!salonId) throw new Error("No location selected.");
    return salonId;
  }, [salonId]);

  const createPreset = useCallback(
    async (body: CreateDiscountPresetRequest) => {
      const forSalon = requireSalonId();
      const created = await createDiscountPreset(forSalon, body);
      if (salonIdRef.current === forSalon) {
        setPresets((prev) =>
          sortDiscountPresets([
            ...prev.filter((preset) => preset.id !== created.id),
            created,
          ]),
        );
      }
      return created;
    },
    [requireSalonId, createDiscountPreset],
  );

  const updatePreset = useCallback(
    async (presetId: string, body: UpdateDiscountPresetRequest) => {
      const forSalon = requireSalonId();
      const updated = await updateDiscountPreset(forSalon, presetId, body);
      if (salonIdRef.current === forSalon) {
        setPresets((prev) =>
          sortDiscountPresets(
            prev.map((preset) => (preset.id === presetId ? updated : preset)),
          ),
        );
      }
      return updated;
    },
    [requireSalonId, updateDiscountPreset],
  );

  const removePreset = useCallback(
    async (presetId: string) => {
      const forSalon = requireSalonId();
      const response = await deleteDiscountPreset(forSalon, presetId);
      if (salonIdRef.current === forSalon) {
        setPresets((prev) => prev.filter((preset) => preset.id !== presetId));
      }
      return response;
    },
    [requireSalonId, deleteDiscountPreset],
  );

  const setQuickDiscounts = useCallback(
    async (presetIds: string[]) => {
      const forSalon = requireSalonId();
      const previousIds = getQuickDiscounts(presetsRef.current).map(
        (preset) => preset.id,
      );
      setPresets((current) => withQuickDiscountOrder(current, presetIds));
      try {
        const updated = await putQuickDiscounts(forSalon, presetIds);
        if (salonIdRef.current === forSalon) {
          setPresets(sortDiscountPresets(updated));
        }
      } catch (err) {
        // Undo only the quick order, so a preset created / edited / deleted
        // while the PUT was in flight survives the rollback.
        if (salonIdRef.current === forSalon) {
          setPresets((current) => withQuickDiscountOrder(current, previousIds));
        }
        throw err;
      }
    },
    [requireSalonId, putQuickDiscounts],
  );

  const quickDiscounts = useMemo(() => getQuickDiscounts(presets), [presets]);

  const value = useMemo<DiscountPresetsState>(
    () => ({
      presets,
      loading,
      error,
      refresh,
      createPreset,
      updatePreset,
      removePreset,
      quickDiscounts,
      setQuickDiscounts,
    }),
    [
      presets,
      loading,
      error,
      refresh,
      createPreset,
      updatePreset,
      removePreset,
      quickDiscounts,
      setQuickDiscounts,
    ],
  );

  return (
    <DiscountPresetsContext.Provider value={value}>
      {children}
    </DiscountPresetsContext.Provider>
  );
}

export function useDiscountPresets(): DiscountPresetsState {
  return useContext(DiscountPresetsContext);
}
